import { FormikErrors, FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { ISettingsValues } from 'types';
import { SimpleGrid, FormSwitch } from 'components/UI';
import ProductFieldTitle from 'components/common/ProductFieldTitle';
import { validateForm } from 'utils/helpers';
import SettingsFormControl from 'components/Settings/SettingsForm/SettingsFormControl';
import SettingsTextContainer from 'components/Settings/SettingsForm/SettingsTextContainer';
import SettingsInput from 'components/Settings/SettingsForm/SettingsInput';
import FieldDescription from 'components/common/FieldDescription';

type GeneralFormProps = {
  values: ISettingsValues | Record<string, never>;
  submitted: boolean;
  errors: FormikErrors<ISettingsValues | Record<string, never>>;
  onBlur: FormikHandlers['handleBlur'];
  onChange: FormikHandlers['handleChange'];
  touched: FormikState<ISettingsValues | Record<string, never>>['touched'];
  setFieldValue: FormikHelpers<ISettingsValues | Record<string, never>>['setFieldValue'];
};

const GeneralForm = ({ values, setFieldValue, onChange, onBlur, ...props }: GeneralFormProps) => {
  const formErrors = validateForm<ISettingsValues | Record<string, never>>(props);

  const renderFlcUrl =
    values?.flcEndpoint && values?.flcEndpoint !== 'false' ? (
      <>
        <SettingsTextContainer>
          <ProductFieldTitle htmlFor="flcEndpointUrl">FLC Integration URL</ProductFieldTitle>
          <FieldDescription>URL required for integration.</FieldDescription>
        </SettingsTextContainer>
        <SettingsFormControl isInvalid={!!formErrors.flcEndpointUrl}>
          <SettingsInput
            name="flcEndpointUrl"
            maxLength={250}
            error={formErrors.flcEndpointUrl}
            value={values.flcEndpointUrl === 'null' ? '' : values.flcEndpointUrl}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter the provided FLC endpoint URL"
          />
        </SettingsFormControl>
      </>
    ) : null;

  return (
    <SimpleGrid columns={2} spacing={4}>
      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="adminEmail">ADMINISTRATOR EMAIL(S) *</ProductFieldTitle>
        <FieldDescription>Email(s) that will receive notifications from the system.</FieldDescription>
      </SettingsTextContainer>
      <SettingsFormControl isInvalid={!!formErrors.adminEmail}>
        <SettingsInput
          name="adminEmail"
          maxLength={254}
          error={formErrors.adminEmail}
          value={values.adminEmail}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Enter the administrator email(s)"
        />
      </SettingsFormControl>
      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="adminRfqEmail">RFQ EMAIL(S) *</ProductFieldTitle>
        <FieldDescription>Email(s) that will receive request for quotes from the system.</FieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.adminRfqEmail}>
        <SettingsInput
          name="adminRfqEmail"
          maxLength={254}
          error={formErrors.adminRfqEmail}
          value={values.adminRfqEmail}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Enter the RFQ email(s)"
        />
      </SettingsFormControl>

      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="registrationApprovalMode">REGISTRATION APPROVAL MODE</ProductFieldTitle>
        <FieldDescription>Require new registrations be manually approved prior to gaining access.</FieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.registrationApprovalMode}>
        <FormSwitch
          name="registrationApprovalMode"
          value={values?.registrationApprovalMode}
          onChange={(e) => setFieldValue('registrationApprovalMode', e.target.checked)}
        />
      </SettingsFormControl>

      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="anonymousAccess">ANONYMOUS ACCESS</ProductFieldTitle>
        <FieldDescription>Allow users to generate configurations without logging in.</FieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.anonymousAccess}>
        <FormSwitch
          name="anonymousAccess"
          value={values?.anonymousAccess}
          onChange={(e) => setFieldValue('anonymousAccess', e.target.checked)}
        />
      </SettingsFormControl>

      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="rfqMode">RFQ MODE</ProductFieldTitle>
        <FieldDescription>Display statistics and enable quote requests.</FieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.rfqMode}>
        <FormSwitch
          name="rfqMode"
          value={values?.rfqMode}
          onChange={(e) => setFieldValue('rfqMode', e.target.checked)}
        />
      </SettingsFormControl>

      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="hideGenerateDownloads">HIDE GENERATE DOWNLOADS</ProductFieldTitle>
        <FieldDescription>
          Controls whether the &quot;Generate Downloads&quot; button in the configurator is displayed.
        </FieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.hideGenerateDownloads}>
        <FormSwitch
          name="hideGenerateDownloads"
          value={values?.hideGenerateDownloads}
          onChange={(e) => setFieldValue('hideGenerateDownloads', e.target.checked)}
        />
      </SettingsFormControl>

      <SettingsTextContainer>
        <ProductFieldTitle htmlFor="flcEndpoint">FLC Integration</ProductFieldTitle>

        <FieldDescription>Activate integration.</FieldDescription>
      </SettingsTextContainer>

      <SettingsFormControl isInvalid={!!formErrors.flcEndpoint}>
        <FormSwitch
          name="flcEndpoint"
          value={values?.flcEndpoint}
          onChange={(e) => setFieldValue('flcEndpoint', e.target.checked)}
        />
      </SettingsFormControl>

      {renderFlcUrl}
    </SimpleGrid>
  );
};

export default GeneralForm;
